import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { Platform } from 'react-native';
import TrackPlayer, {
  Track,
  State,
  Event,
  usePlaybackState,
  useProgress,
  useTrackPlayerEvents,
  Capability,
  AppKilledPlaybackBehavior,
  RepeatMode,
} from 'react-native-track-player';
import { FireBook, FireInsight } from '../models/FirestoreModels';
import InstallAppPrompt from '../components/ui/InstallAppPrompt';

export interface AudioSegment {
  id: string;
  title: string;
  type: 'introduction' | 'insight';
  audioUrl?: string;
  duration: number;
  data?: any; // FireBook for introduction, FireInsight for insights
}

export interface AudioPlayerState {
  // Current playing info
  currentSegment: AudioSegment | null;
  currentIndex: number;
  playlist: AudioSegment[];
  
  // Playback state
  isPlaying: boolean;
  isLoading: boolean;
  position: number; // in milliseconds
  duration: number; // in milliseconds
  playbackRate: number;
  
  // Book info
  book: FireBook | null;
  insights: FireInsight[];
  
  // UI state
  isFullPlayerVisible: boolean;
  isMinimalPlayerVisible: boolean;
}

interface AudioPlayerContextType extends AudioPlayerState {
  // Controls
  play: () => Promise<void>;
  pause: () => Promise<void>;
  playPause: () => Promise<void>;
  seekTo: (position: number) => Promise<void>;
  playNext: () => Promise<void>;
  playPrevious: () => Promise<void>;
  setPlaybackRate: (rate: number) => Promise<void>;
  
  // Navigation
  jumpToSegment: (index: number) => Promise<void>;
  
  // Playlist management
  loadBookAudio: (book: FireBook, insights: FireInsight[]) => Promise<{ hasIntroduction: boolean }>;
  loadInsightsOnly: (insights: FireInsight[], book?: FireBook, includeIntroduction?: boolean, forceRebuild?: boolean) => Promise<void>;
  loadMixedInsights: (insights: FireInsight[], forceRebuild?: boolean) => Promise<void>;
  clearPlaylist: () => void;
  
  // UI controls
  showFullPlayer: () => void;
  hideFullPlayer: () => void;
  showMinimalPlayer: () => void;
  hideMinimalPlayer: () => void;

  // Web install prompt
  isInstallPromptVisible: boolean;
  showInstallPrompt: () => void;
  hideInstallPrompt: () => void;
}

const defaultState: AudioPlayerState = {
  currentSegment: null,
  currentIndex: -1,
  playlist: [],
  isPlaying: false,
  isLoading: false,
  position: 0,
  duration: 0,
  playbackRate: 1.0,
  book: null,
  insights: [],
  isFullPlayerVisible: false,
  isMinimalPlayerVisible: false,
};

const AudioPlayerContext = createContext<AudioPlayerContextType | null>(null);

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  }
  return context;
};

interface AudioPlayerProviderProps {
  children: ReactNode;
}

// Setup TrackPlayer
export const setupPlayer = async () => {
  if (Platform.OS === 'web') {
    return;
  }
  let isSetup = false;
  try {
    // Check if already setup
    const currentTrack = await TrackPlayer.getActiveTrack();
    isSetup = true;
    console.log('✅ TrackPlayer already setup');
  } catch {
    // Not setup yet
  }

  if (!isSetup) {
    try {
      // Register playback service first
      const { PlaybackService } = require('../services/playbackService');
      TrackPlayer.registerPlaybackService(() => PlaybackService);
      
      await TrackPlayer.setupPlayer();
      
      await TrackPlayer.updateOptions({
        capabilities: [
          Capability.Play,
          Capability.Pause,
          Capability.SkipToNext,
          Capability.SkipToPrevious,
          Capability.SeekTo,
        ],
        compactCapabilities: [
          Capability.Play,
          Capability.Pause,
          Capability.SkipToNext,
        ],
        progressUpdateEventInterval: 1,
        android: {
          appKilledPlaybackBehavior: AppKilledPlaybackBehavior.ContinuePlayback,
        },
      });
      
      console.log('✅ TrackPlayer setup complete');
    } catch (error) {
      console.error('❌ Error setting up TrackPlayer:', error);
    }
  }
};

export const AudioPlayerProvider: React.FC<AudioPlayerProviderProps> = ({ children }) => {
  const [state, setState] = useState<AudioPlayerState>(defaultState);
  const [isInstallPromptVisible, setIsInstallPromptVisible] = useState(false);
  const playbackState = usePlaybackState();
  const progress = useProgress();
  const isInitializedRef = useRef(false);

  // Initialize TrackPlayer on mount
  useEffect(() => {
    const initPlayer = async () => {
      if (!isInitializedRef.current) {
        if (Platform.OS !== 'web') {
          await setupPlayer();
          isInitializedRef.current = true;
        }
      }
    };
    
    initPlayer();
  }, []);

  // Update state based on playback state
  useEffect(() => {
    const state = playbackState.state;
    const isPlaying = state === State.Playing;
    const isLoading = state === State.Buffering || state === State.Loading || state === State.Connecting;
    
    setState(prev => ({
      ...prev,
      isPlaying,
      isLoading,
    }));
  }, [playbackState.state]);

  // Update progress
  useEffect(() => {
    setState(prev => ({
      ...prev,
      position: progress.position * 1000, // Convert to milliseconds
      duration: progress.duration * 1000, // Convert to milliseconds
    }));
  }, [progress.position, progress.duration]);

  // Listen to track changes
  useTrackPlayerEvents([Event.PlaybackActiveTrackChanged], async (event) => {
    if (event.type === Event.PlaybackActiveTrackChanged && event.index !== undefined && event.index !== null) {
      const track = await TrackPlayer.getTrack(event.index);
      if (track && state.playlist[event.index]) {
        setState(prev => ({
          ...prev,
          currentIndex: event.index!,
          currentSegment: prev.playlist[event.index!],
        }));
      }
    }
  });

  // Convert AudioSegment to TrackPlayer Track
  const segmentToTrack = (segment: AudioSegment, index: number, book?: FireBook): Track => {
    const bookInfo = segment.type === 'insight' && segment.data?.book ? segment.data.book : book;
    const artwork = bookInfo?.coverImageUrl || bookInfo?.coverImageFullUrl || undefined;
    
    return {
      id: segment.id,
      url: segment.audioUrl || '',
      title: segment.title,
      artist: bookInfo?.author || 'Mentorist AI',
      album: bookInfo?.title || 'Personal Development',
      artwork: artwork || undefined,
      duration: segment.duration / 1000, // Convert to seconds
    };
  };

  // Control functions
  const play = async () => {
    if (Platform.OS === 'web') {
      setIsInstallPromptVisible(true);
      return;
    }
    await TrackPlayer.play();
    setState(prev => ({ 
      ...prev, 
      isPlaying: true,
      isMinimalPlayerVisible: true,
    }));
  };

  const pause = async () => {
    if (Platform.OS === 'web') {
      return;
    }
    await TrackPlayer.pause();
    setState(prev => ({ ...prev, isPlaying: false }));
  };

  const playPause = async () => {
    if (Platform.OS === 'web') {
      setIsInstallPromptVisible(true);
      return;
    }
    const currentState = await TrackPlayer.getPlaybackState();
    if (currentState.state === State.Playing) {
      await pause();
    } else {
      await play();
    }
  };

  const seekTo = async (position: number) => {
    if (Platform.OS === 'web') {
      return;
    }
    await TrackPlayer.seekTo(position / 1000); // Convert to seconds
  };

  const setPlaybackRate = async (rate: number) => {
    if (Platform.OS === 'web') {
      return;
    }
    await TrackPlayer.setRate(rate);
    setState(prev => ({ ...prev, playbackRate: rate }));
  };

  const jumpToSegment = async (index: number) => {
    if (Platform.OS === 'web') {
      setIsInstallPromptVisible(true);
      return;
    }
    if (index < 0 || index >= state.playlist.length) return;
    
    await TrackPlayer.skip(index);
    await play();
    
    setState(prev => ({
      ...prev,
      currentIndex: index,
      currentSegment: prev.playlist[index],
    }));
  };

  const playNext = async () => {
    if (Platform.OS === 'web') {
      return;
    }
    await TrackPlayer.skipToNext();
  };

  const playPrevious = async () => {
    if (Platform.OS === 'web') {
      return;
    }
    await TrackPlayer.skipToPrevious();
  };

  // Helper function to check if insights match current playlist
  const playlistMatchesInsights = (insights: FireInsight[]) => {
    const currentInsightIds = state.playlist
      .filter(seg => seg.type === 'insight')
      .map(seg => seg.id)
      .sort();
    const newInsightIds = insights.map(i => i.id).sort();
    
    if (currentInsightIds.length !== newInsightIds.length) return false;
    return currentInsightIds.every((id, index) => id === newInsightIds[index]);
  };

  // Build playlist from book and insights
  const loadBookAudio = async (book: FireBook, insights: FireInsight[]) => {
    if (Platform.OS === 'web') {
      setIsInstallPromptVisible(true);
      return { hasIntroduction: false };
    }
    const bookHasIntro = !!(book.audioSegments?.book_intro || book.audioSegments?.introduction);
    const expectedLength = insights.length + (bookHasIntro ? 1 : 0);
    
    const currentHasIntro = state.playlist.some(seg => seg.type === 'introduction' && seg.data?.id === book.id);
    const lengthMatches = state.playlist.length === expectedLength;
    
    if (currentHasIntro && playlistMatchesInsights(insights) && state.book?.id === book.id && lengthMatches) {
      return { hasIntroduction: currentHasIntro };
    }
    
    await TrackPlayer.reset();
    
    const playlist: AudioSegment[] = [];
    const tracks: Track[] = [];
    let hasIntroduction = false;
    
    // Add introduction if available
    if (book.audioSegments) {
      const introSegment = book.audioSegments.book_intro || book.audioSegments.introduction;
      if (introSegment) {
        const introAudio = introSegment.male || introSegment.female || introSegment;
        if (introAudio?.url) {
          hasIntroduction = true;
          const segment: AudioSegment = {
            id: 'introduction',
            title: 'Introduction',
            type: 'introduction',
            audioUrl: introAudio.url,
            duration: (introAudio.duration || 0) * 1000,
            data: book,
          };
          playlist.push(segment);
          tracks.push(segmentToTrack(segment, tracks.length, book));
        }
      }
    }
    
    // Add insights
    insights.forEach((insight) => {
      if (insight.audioSegments) {
        const storySegment = insight.audioSegments.insight_story || insight.audioSegments.story;
        if (storySegment) {
          const insightAudio = storySegment.male || storySegment.female || storySegment;
          if (insightAudio?.url) {
            const segment: AudioSegment = {
              id: insight.id,
              title: insight.title,
              type: 'insight',
              audioUrl: insightAudio.url,
              duration: (insightAudio.duration || 0) * 1000,
              data: insight,
            };
            playlist.push(segment);
            tracks.push(segmentToTrack(segment, tracks.length, book));
          }
        }
      }
    });
    
    if (tracks.length > 0) {
      await TrackPlayer.add(tracks);
    }
    
    setState(prev => ({
      ...prev,
      playlist,
      book,
      insights,
      currentIndex: -1,
      currentSegment: null,
      isMinimalPlayerVisible: true,
    }));
    
    return { hasIntroduction };
  };

  // Load only insights
  const loadInsightsOnly = async (insights: FireInsight[], book?: FireBook, includeIntroduction: boolean = true, forceRebuild: boolean = false) => {
    if (Platform.OS === 'web') {
      setIsInstallPromptVisible(true);
      return;
    }
    if (!forceRebuild) {
      const hasIntro = state.playlist.some(seg => seg.type === 'introduction');
      const shouldSkip = playlistMatchesInsights(insights) && 
                        state.book?.id === book?.id && 
                        hasIntro === includeIntroduction;
      
      if (shouldSkip) {
        return;
      }
    }
    
    await TrackPlayer.reset();
    
    const playlist: AudioSegment[] = [];
    const tracks: Track[] = [];
    
    // Add introduction if requested
    if (includeIntroduction && book?.audioSegments) {
      const introSegment = book.audioSegments.book_intro || book.audioSegments.introduction;
      if (introSegment) {
        const introAudio = introSegment.male || introSegment.female || introSegment;
        if (introAudio?.url) {
          const segment: AudioSegment = {
            id: 'introduction',
            title: 'Introduction',
            type: 'introduction',
            audioUrl: introAudio.url,
            duration: (introAudio.duration || 0) * 1000,
            data: book,
          };
          playlist.push(segment);
          tracks.push(segmentToTrack(segment, tracks.length, book));
        }
      }
    }
    
    // Add insights
    insights.forEach((insight) => {
      if (insight.audioSegments) {
        const storySegment = insight.audioSegments.insight_story || insight.audioSegments.story;
        if (storySegment) {
          const insightAudio = storySegment.male || storySegment.female || storySegment;
          if (insightAudio?.url) {
            const segment: AudioSegment = {
              id: insight.id,
              title: insight.title,
              type: 'insight',
              audioUrl: insightAudio.url,
              duration: (insightAudio.duration || 0) * 1000,
              data: insight,
            };
            playlist.push(segment);
            tracks.push(segmentToTrack(segment, tracks.length, book));
          }
        }
      }
    });
    
    if (tracks.length > 0) {
      await TrackPlayer.add(tracks);
    }
    
    setState(prev => ({
      ...prev,
      playlist,
      book: book || null,
      insights,
      currentIndex: -1,
      currentSegment: null,
      isMinimalPlayerVisible: true,
    }));
  };

  // Load mixed insights from various books
  const loadMixedInsights = async (insights: FireInsight[], forceRebuild: boolean = false) => {
    if (Platform.OS === 'web') {
      setIsInstallPromptVisible(true);
      return;
    }
    if (!forceRebuild && playlistMatchesInsights(insights)) {
      return;
    }
    
    await TrackPlayer.reset();
    
    const playlist: AudioSegment[] = [];
    const tracks: Track[] = [];
    
    insights.forEach((insight) => {
      if (insight.audioSegments) {
        const storySegment = insight.audioSegments.insight_story || insight.audioSegments.story;
        if (storySegment) {
          const insightAudio = storySegment.male || storySegment.female || storySegment;
          if (insightAudio?.url) {
            const segment: AudioSegment = {
              id: insight.id,
              title: insight.title,
              type: 'insight',
              audioUrl: insightAudio.url,
              duration: (insightAudio.duration || 0) * 1000,
              data: insight,
            };
            playlist.push(segment);
            tracks.push(segmentToTrack(segment, tracks.length));
          }
        }
      }
    });
    
    if (tracks.length > 0) {
      await TrackPlayer.add(tracks);
    }
    
    setState(prev => ({
      ...prev,
      playlist,
      book: null,
      insights,
      currentIndex: -1,
      currentSegment: null,
      isMinimalPlayerVisible: true,
    }));
  };

  const clearPlaylist = async () => {
    if (Platform.OS === 'web') {
      setState(defaultState);
      return;
    }
    await TrackPlayer.reset();
    setState(defaultState);
  };

  // UI control functions
  const showFullPlayer = () => setState(prev => ({ ...prev, isFullPlayerVisible: true }));
  const hideFullPlayer = () => setState(prev => ({ 
    ...prev, 
    isFullPlayerVisible: false,
    isMinimalPlayerVisible: true,
  }));
  const showMinimalPlayer = () => setState(prev => ({ ...prev, isMinimalPlayerVisible: true }));
  const hideMinimalPlayer = () => setState(prev => ({ ...prev, isMinimalPlayerVisible: false }));

  const contextValue: AudioPlayerContextType = {
    ...state,
    play,
    pause,
    playPause,
    seekTo,
    playNext,
    playPrevious,
    setPlaybackRate,
    jumpToSegment,
    loadBookAudio,
    loadInsightsOnly,
    loadMixedInsights,
    clearPlaylist,
    showFullPlayer,
    hideFullPlayer,
    showMinimalPlayer,
    hideMinimalPlayer,
    isInstallPromptVisible,
    showInstallPrompt: () => setIsInstallPromptVisible(true),
    hideInstallPrompt: () => setIsInstallPromptVisible(false),
  };

  return (
    <AudioPlayerContext.Provider value={contextValue}>
      {children}
      {Platform.OS === 'web' && (
        <InstallAppPrompt
          visible={isInstallPromptVisible}
          onDismiss={() => setIsInstallPromptVisible(false)}
        />
      )}
    </AudioPlayerContext.Provider>
  );
};

export default AudioPlayerContext;
